import { Fragment, useState } from 'react';
import { Modal } from '../../components/common';
import { IconCamera, IconTrash } from '../../components/icons';
import { Switch } from './shell';

/**
 * สร้างบริการใหม่ — built from tools/captures/dialog-service.json, which was
 * read out of the live POS's DOM rather than guessed at.
 *
 * The shape that matters is the เวลาบริการ block: a service carries as many
 * durations as the shop sells it in, which is one service_variant each. The
 * flat single-duration form this replaces could only ever describe the first.
 */

const SERVICE_TYPES = [
  { value: 'single', label: 'บริการเดี่ยว' },
  { value: 'combo', label: 'บริการคอมโบ' },
  { value: 'four_hands', label: 'บริการ 4 Hands' },
];

const GUEST_COUNTS = [
  { value: 1, label: 'สำหรับลูกค้าคนเดียว' },
  { value: 2, label: 'สำหรับลูกค้า 2 คน' },
];

const BLANK_ROW = {
  id: null, basePrice: '0', baseCost: '0', durationMinutes: '0',
  baseCommission: '0', agencyPrices: {},
};

const n = (value) => Number(value) || 0;
const MAX_IMAGE_SOURCE_BYTES = 12 * 1024 * 1024;
const MAX_IMAGE_STORED_BYTES = 450 * 1024;

/* The swatch is a native colour input and rejects anything that is not #rrggbb,
   while the box beside it takes whatever the shop pastes in. */
const HEX = /^#[0-9a-f]{6}$/i;

const imageDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('ไม่สามารถอ่านไฟล์รูปภาพได้'));
  reader.readAsDataURL(file);
});

const resizedImageDataUrl = async (file) => {
  if (file.size <= MAX_IMAGE_STORED_BYTES) return imageDataUrl(file);

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('ไม่สามารถเปิดไฟล์รูปบริการได้'));
      element.src = objectUrl;
    });
    const maxSide = 900;
    const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    let quality = 0.82;
    let result = canvas.toDataURL('image/jpeg', quality);
    while (result.length > 600000 && quality > 0.5) {
      quality -= 0.08;
      result = canvas.toDataURL('image/jpeg', quality);
    }
    return result;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const Radios = ({ label, value, options, onChange }) => (
  <div className="field">
    <label>{label}</label>
    <div className="choice-opts">
      {options.map((o) => (
        <label key={o.value} className={`radio${value === o.value ? ' on' : ''}`}>
          <input type="radio" checked={value === o.value} onChange={() => onChange(o.value)} />
          {o.label}
        </label>
      ))}
    </div>
  </div>
);

const Text = ({ label, value, onChange, ...rest }) => (
  <div className="field">
    <label>{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} {...rest} />
  </div>
);

const Area = ({ label, value, onChange }) => (
  <div className="field">
    <label>{label}</label>
    <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

/**
 * @param service  the row being edited, or null when creating
 * @param isAddon  which of the บริการ / บริการเสริม tabs the button was pressed on
 */
export default function ServiceDialog({
  service, isAddon, categoryId, categories, bedTypes, agencies, addonServices,
  onSubmit, onClose,
}) {
  const [v, setV] = useState(() => {
    const rows = (service?.variants || []).filter((r) => r.isActive !== false);
    return {
      imageUrl: service?.image_url || '',
      color: service?.color || '#2f4a3c',
      /* Their dialog carries one ชื่อบริการ where ours splits name_en/name_th,
         so the box holds the whole name and saving folds the two together —
         the row then reads back exactly what was typed into it. */
      name: [service?.name_en, service?.name_th].filter(Boolean).join(' '),
      categoryId: service?.category_id ?? categoryId ?? '',
      serviceType: service?.service_type || 'single',
      guestCount: service?.guest_count ?? 1,
      bedTypeIds: (service?.bed_type_ids || []).map(Number),
      description: service?.description || '',
      webNameTh: service?.web_name_th || '',
      webNameEn: service?.web_name_en || '',
      webDescriptionTh: service?.web_description_th || '',
      webDescriptionEn: service?.web_description_en || '',
      webCautionTh: service?.web_caution_th || '',
      webCautionEn: service?.web_caution_en || '',
      rows: rows.length ? rows.map((r) => ({
        id: r.id,
        basePrice: String(r.basePrice ?? ''),
        baseCost: String(r.baseCost ?? ''),
        durationMinutes: String(r.durationMinutes ?? ''),
        baseCommission: String(r.baseCommission ?? ''),
        agencyPrices: r.agencyPrices || {},
      })) : [{ ...BLANK_ROW }],
      addonServiceIds: (service?.addon_service_ids || []).map(String),
      prepBeforeMinutes: String(service?.prep_before_minutes ?? 0),
      prepAfterMinutes: String(service?.prep_after_minutes ?? 0),
      allTherapists: service ? service.all_therapists !== false : true,
      isActive: service ? service.is_active !== false : true,
      priceEditable: service ? !!service.price_editable : false,
    };
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setV((s) => ({ ...s, [key]: value }));
  const setRow = (i, patch) => setV((s) => ({
    ...s,
    rows: s.rows.map((r, j) => (i === j ? { ...r, ...patch } : r)),
  }));

  const toggleBed = (id) => set(
    'bedTypeIds',
    v.bedTypeIds.includes(id) ? v.bedTypeIds.filter((x) => x !== id) : [...v.bedTypeIds, id],
  );

  const chooseImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('กรุณาเลือกไฟล์รูปภาพของบริการ');
      return;
    }
    if (file.size > MAX_IMAGE_SOURCE_BYTES) {
      setError('รูปบริการต้องมีขนาดไม่เกิน 12 MB');
      return;
    }

    try {
      set('imageUrl', await resizedImageDataUrl(file));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const submit = async () => {
    if (!v.name.trim()) {
      setError('กรุณากรอกชื่อบริการ');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        nameEn: v.name.trim(),
        nameTh: null,
        categoryId: Number(v.categoryId) || null,
        description: v.description || null,
        imageUrl: v.imageUrl || null,
        color: v.color || null,
        isAddon,
        serviceType: v.serviceType,
        guestCount: v.guestCount,
        prepBeforeMinutes: n(v.prepBeforeMinutes),
        prepAfterMinutes: n(v.prepAfterMinutes),
        allTherapists: v.allTherapists,
        isActive: v.isActive,
        priceEditable: v.priceEditable,
        webNameTh: v.webNameTh || null,
        webNameEn: v.webNameEn || null,
        webDescriptionTh: v.webDescriptionTh || null,
        webDescriptionEn: v.webDescriptionEn || null,
        webCautionTh: v.webCautionTh || null,
        webCautionEn: v.webCautionEn || null,
        bedTypeIds: v.bedTypeIds,
        addonServiceIds: v.addonServiceIds.filter(Boolean).map(Number),
        variants: v.rows.map((r) => ({
          id: r.id,
          durationMinutes: n(r.durationMinutes),
          basePrice: n(r.basePrice),
          baseCost: n(r.baseCost),
          baseCommission: n(r.baseCommission),
          agencyPrices: r.agencyPrices,
        })),
      });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      /* Only the create title was captured; แก้ไขบริการ mirrors how the other
         ตั้งค่า dialogs name themselves. */
      title={service ? 'แก้ไขบริการ' : 'สร้างบริการใหม่'}
      className="service-modal"
      onClose={onClose}
      footer={(
        <>
          <button className="btn-outline" onClick={onClose} disabled={busy}>ยกเลิก</button>
          <button className="btn-solid" onClick={submit} disabled={busy}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      )}
    >
      {error && <div className="login-error">{error}</div>}

      <div className="svc-section">
        <div className="svc-top">
          <div className="svc-fields">
            <label className="svc-photo settings-photo-upload" title="อัปโหลดรูปบริการ">
              {v.imageUrl ? <img src={v.imageUrl} alt="" /> : null}
              <span className="badge-cam"><IconCamera size={14} /></span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                aria-label="อัปโหลดรูปบริการ"
                onChange={chooseImage}
              />
            </label>
            <span className="hint settings-photo-hint">กดที่รูปเพื่ออัปโหลด · ไม่เกิน 12 MB</span>
            {v.imageUrl && (
              <button
                type="button"
                className="btn-outline settings-photo-remove"
                onClick={() => set('imageUrl', '')}
              >
                ลบรูป
              </button>
            )}
            <div className="field">
              <label>สี</label>
              <div className="svc-colour">
                <input
                  type="color"
                  value={HEX.test(v.color) ? v.color : '#2f4a3c'}
                  onChange={(e) => set('color', e.target.value)}
                />
                <input
                  value={v.color}
                  onChange={(e) => set('color', e.target.value)}
                  style={{ flex: 1, minWidth: 0 }}
                />
              </div>
            </div>
          </div>

          <div className="svc-fields">
            <Text label="ชื่อบริการ" value={v.name} onChange={(x) => set('name', x)} />

            <div className="field">
              <label>กลุ่มบริการ</label>
              <select value={v.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
                <option value="">ไม่ระบุกลุ่ม</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name_th || c.name_en}</option>
                ))}
              </select>
            </div>

            <Radios
              label="ประเภทบริการ"
              value={v.serviceType}
              options={SERVICE_TYPES}
              onChange={(x) => set('serviceType', x)}
            />
            <Radios
              label="จำนวนลูกค้า"
              value={v.guestCount}
              options={GUEST_COUNTS}
              onChange={(x) => set('guestCount', x)}
            />

            {/* Theirs lists individual beds. Ours are per branch while a service
                belongs to the whole shop, so the list is bed types — which is
                also what already decides รองรับบริการนี้ on the board. */}
            <div className="field">
              <label>เพิ่มเตียงสำหรับบริการนี้</label>
              {bedTypes.length ? (
                <div className="multiselect">
                  {bedTypes.map((b) => (
                    <button
                      type="button"
                      key={b.id}
                      className={`staff-chip small${v.bedTypeIds.includes(b.id) ? '' : ' off'}`}
                      onClick={() => toggleBed(b.id)}
                    >
                      {b.name_th || b.name_en}
                    </button>
                  ))}
                </div>
              ) : (
                <span className="hint">Select Beds</span>
              )}
            </div>

            <Area
              label="คำอธิบายบริการ"
              value={v.description}
              onChange={(x) => set('description', x)}
            />
          </div>
        </div>
      </div>

      <div className="svc-section">
        <h3>ตั้งค่าแสดงผลบนเว็บไซต์</h3>
        <div className="grid-2">
          <Text label="ชื่อบริการ ภาษาไทย" value={v.webNameTh} onChange={(x) => set('webNameTh', x)} />
          <Text label="ชื่อบริการ ภาษาอังกฤษ" value={v.webNameEn} onChange={(x) => set('webNameEn', x)} />
          <Area label="คำอธิบายบริการ ภาษาไทย" value={v.webDescriptionTh} onChange={(x) => set('webDescriptionTh', x)} />
          <Area label="คำอธิบายบริการ ภาษาอังกฤษ" value={v.webDescriptionEn} onChange={(x) => set('webDescriptionEn', x)} />
          <Area label="ข้อควรระวัง ภาษาไทย" value={v.webCautionTh} onChange={(x) => set('webCautionTh', x)} />
          <Area label="ข้อควรระวัง ภาษาอังกฤษ" value={v.webCautionEn} onChange={(x) => set('webCautionEn', x)} />
        </div>
      </div>

      <div className="svc-section">
        <h3>เวลาบริการ</h3>
        <h4>ช่วงเวลา และราคาของบริการ</h4>
        <div className="svc-grid svc-time">
          <span className="svc-head">ราคา</span>
          {/* ตันทุน is their spelling, kept as it reads on their screen. */}
          <span className="svc-head">ตันทุน</span>
          <span className="svc-head">เวลา (นาที)</span>
          <span />

          {/* Cells of one grid rather than a row element each, so every column
              lines up straight down the block. */}
          {v.rows.map((r, i) => (
            <Fragment key={r.id ?? `new-${i}`}>
              <input value={r.basePrice} onChange={(e) => setRow(i, { basePrice: e.target.value })} />
              <input value={r.baseCost} onChange={(e) => setRow(i, { baseCost: e.target.value })} />
              <input value={r.durationMinutes} onChange={(e) => setRow(i, { durationMinutes: e.target.value })} />
              {/* Removing a row was not captured — a repeating list the shop
                  can only ever grow is not one they could correct. */}
              <button
                type="button"
                className="svc-drop"
                title="ลบช่วงเวลา"
                disabled={v.rows.length === 1}
                onClick={() => set('rows', v.rows.filter((_, j) => j !== i))}
              >
                <IconTrash size={15} />
              </button>
            </Fragment>
          ))}
        </div>
        <button
          type="button"
          className="svc-add"
          onClick={() => set('rows', [...v.rows, { ...BLANK_ROW }])}
        >
          เพิ่มช่วงเวลา
        </button>
      </div>

      <div className="svc-section">
        <h3>บริการเสริม</h3>
        {/* The capture recorded the section and its add button but no columns,
            so a row is the add-on itself — one of the services the บริการเสริม
            tab already holds. */}
        <div className="svc-addons">
          {/* Keyed by position: a row has no identity until it names a service,
              and keying by the choice would remount the select under the cursor
              the moment it was made. */}
          {v.addonServiceIds.map((id, i) => (
            <div className="svc-addon-row" key={i}>
              <select
                value={id}
                onChange={(e) => set(
                  'addonServiceIds',
                  v.addonServiceIds.map((x, j) => (i === j ? e.target.value : x)),
                )}
              >
                <option value="">เลือกบริการเสริม</option>
                {addonServices.map((a) => (
                  <option key={a.id} value={a.id}>
                    {[a.name_en, a.name_th].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="svc-drop"
                title="ลบบริการเสริม"
                onClick={() => set('addonServiceIds', v.addonServiceIds.filter((_, j) => j !== i))}
              >
                <IconTrash size={15} />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="svc-add"
          onClick={() => set('addonServiceIds', [...v.addonServiceIds, ''])}
        >
          เพิ่มบริการเสริม
        </button>
      </div>

      <div className="svc-section">
        <h3>ราคาเอเจนซี่</h3>
        {agencies.length ? (
          <div className="svc-agency-scroll">
            <div
              className="svc-grid svc-agency"
              style={{ gridTemplateColumns: `88px repeat(${agencies.length}, 120px)` }}
            >
              <span className="svc-head">เวลา</span>
              {agencies.map((a) => <span className="svc-head" key={a.id}>{a.name}</span>)}

              {v.rows.map((r, i) => (
                <Fragment key={r.id ?? `new-${i}`}>
                  <span className="svc-time-label">{n(r.durationMinutes)} นาที</span>
                  {agencies.map((a) => (
                    <input
                      key={a.id}
                      placeholder="ราคา"
                      value={r.agencyPrices[a.id] ?? ''}
                      onChange={(e) => setRow(i, {
                        agencyPrices: { ...r.agencyPrices, [a.id]: e.target.value },
                      })}
                    />
                  ))}
                </Fragment>
              ))}
            </div>
          </div>
        ) : (
          /* Their first column is their own brand — the house price, which for
             us is the ราคา column above. The rest come from the agency list on
             ตั้งค่า → ช่องทางการจอง, so with none set up there is no matrix. */
          <span className="hint">ยังไม่มีเอเจนซี่ — เพิ่มได้ที่ ตั้งค่า → ช่องทางการจอง</span>
        )}
      </div>

      <div className="svc-section">
        <h3>เวลาเตรียมการ</h3>
        <div className="grid-2">
          <Text
            label="เวลาเตรียมการเริ่มต้น (นาที)"
            value={v.prepBeforeMinutes}
            onChange={(x) => set('prepBeforeMinutes', x)}
          />
          <Text
            label="เวลาเตรียมการสิ้นสุด (นาที)"
            value={v.prepAfterMinutes}
            onChange={(x) => set('prepAfterMinutes', x)}
          />
        </div>
      </div>

      <div className="svc-section">
        <Switch
          label="ใช้กับทั้งหมด"
          hint="ทำได้บางคน / บริการทั่วไปทำได้ทุกคน"
          on={v.allTherapists}
          onChange={(x) => set('allTherapists', x)}
        />
        <Switch
          label="สถานะ"
          hint="ปิด / เปิด"
          on={v.isActive}
          onChange={(x) => set('isActive', x)}
        />
        <Switch
          label="สามารถปรับราคาบริการก่อนชำระเงินได้"
          hint="ไม่สามารถปรับได้ / สามารถปรับได้"
          on={v.priceEditable}
          onChange={(x) => set('priceEditable', x)}
        />
      </div>
    </Modal>
  );
}
